import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"
import {
  deriveAreaBreakdown,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"

/**
 * Everything the area-intelligence rows need for ONE interest area: fleet
 * volume with product split and momentum, plus the two lists sales works
 * from — who uses the capability (references, webinar invites) and which
 * ACTIVE accounts don't (the expansion campaign list) — each priced with
 * the 12-month billed revenue riding on it.
 */
export type PortfolioAreaIntel = {
  area: string
  /** Weighted fleet volume, last 28 days. */
  current: number
  /** Same measure, the 28 days before. */
  previous: number
  trendPct: number | null
  /** Current volume per product (v1/v2), for the stacked bar. */
  appSplit: { app: string; current: number }[]
  /** Daily weighted totals, last 28 days oldest first (sparkline). */
  daily: number[]
  /** Customers using the area, heaviest first, with their area volume. */
  adopters: { customer: PortfolioCustomerSummary; volume: number }[]
  /** ACTIVE customers with no tracked use of the area, biggest books first. */
  whitespace: PortfolioCustomerSummary[]
  /**
   * Users with this area among their top areas — the feature-adoption
   * numerator (denominator: the fleet's monthly actives). A floor, not an
   * exact count: profiles carry each user's top 3 areas only, so broad
   * users' minor areas go uncounted.
   */
  userAdopters: number
  /** 12-month billed revenue of the adopters — the money using this. */
  adopterBilled12M: number
  /** 12-month billed revenue of the whitespace — paying money to pitch. */
  whitespaceBilled12M: number
  /** Display currency (first seen among the lists' customers). */
  currency: string | null
}

const AREA_DAILY_WINDOW_DAYS = 28

/**
 * Joins the fleet daily rows (exact volumes/trends/split) with the member
 * profiles (who touches what — same top-areas source the per-customer
 * "Nobody has touched X" plays read, so this section and the playbook
 * always tell the same story).
 */
export function deriveAreaIntelligence(
  areaDays: UsageAreaDayRow[],
  customers: PortfolioCustomerSummary[],
): PortfolioAreaIntel[] {
  const base = deriveAreaBreakdown(areaDays)

  // Per-area: per-app current volume + per-day series (last 28 days).
  const now = Date.now()
  const from28d = new Date(now - AREA_DAILY_WINDOW_DAYS * ONE_DAY_IN_MS)
  const appByArea = new Map<string, Map<string, number>>()
  const dailyByArea = new Map<string, Map<string, number>>()
  for (const row of areaDays) {
    const day = String(row.Date).slice(0, 10)
    const date = new Date(day)
    if (date < from28d) continue
    const value = row.WeightedCount ?? row.EventCount

    const apps = appByArea.get(row.AppArea) ?? new Map<string, number>()
    const app = row.AppCode ?? "v2-console"
    apps.set(app, (apps.get(app) ?? 0) + value)
    appByArea.set(row.AppArea, apps)

    const days = dailyByArea.get(row.AppArea) ?? new Map<string, number>()
    days.set(day, (days.get(day) ?? 0) + value)
    dailyByArea.set(row.AppArea, days)
  }

  // Per-customer area volumes from the member profiles.
  const adoptersByArea = new Map<
    string,
    { customer: PortfolioCustomerSummary; volume: number }[]
  >()
  const touchedByCustomer = new Map<string, Set<string>>()
  const userAdoptersByArea = new Map<string, number>()
  for (const customer of customers) {
    const volumes = new Map<string, number>()
    for (const member of customer.members) {
      for (const topArea of member.TopAreas28d) {
        volumes.set(
          topArea.Area,
          (volumes.get(topArea.Area) ?? 0) +
            (topArea.Weighted ?? topArea.Count),
        )
        // A member lists an area at most once, so a plain counter is a
        // distinct-user count.
        userAdoptersByArea.set(
          topArea.Area,
          (userAdoptersByArea.get(topArea.Area) ?? 0) + 1,
        )
      }
    }
    touchedByCustomer.set(customer.externalCustomerId, new Set(volumes.keys()))
    for (const [area, volume] of volumes) {
      const list = adoptersByArea.get(area) ?? []
      list.push({ customer, volume })
      adoptersByArea.set(area, list)
    }
  }

  const activeCustomers = customers.filter(
    (customer) => customer.summary.activeUsers28d > 0,
  )

  return base.map((entry) => {
    const adopters = (adoptersByArea.get(entry.area) ?? []).sort(
      (a, b) => b.volume - a.volume,
    )
    // Biggest books first: the campaign list is worked top-down, and money
    // already paying beats seat count as the "worth a call" order.
    const whitespace = activeCustomers
      .filter(
        (customer) =>
          !touchedByCustomer.get(customer.externalCustomerId)?.has(entry.area),
      )
      .sort(
        (a, b) =>
          (b.billed12M ?? -1) - (a.billed12M ?? -1) ||
          b.summary.seatCount - a.summary.seatCount,
      )

    const dayMap = dailyByArea.get(entry.area)
    const daily: number[] = []
    for (let i = AREA_DAILY_WINDOW_DAYS - 1; i >= 0; i -= 1) {
      const key = new Date(now - i * ONE_DAY_IN_MS).toISOString().slice(0, 10)
      daily.push(Math.round(dayMap?.get(key) ?? 0))
    }

    return {
      area: entry.area,
      current: entry.current,
      previous: entry.previous,
      trendPct: entry.trendPct,
      appSplit: [...(appByArea.get(entry.area) ?? new Map())]
        .map(([app, current]) => ({ app, current }))
        .sort((a, b) => a.app.localeCompare(b.app)),
      daily,
      adopters,
      whitespace,
      userAdopters: userAdoptersByArea.get(entry.area) ?? 0,
      adopterBilled12M: adopters.reduce(
        (sum, adopter) => sum + (adopter.customer.billed12M ?? 0),
        0,
      ),
      whitespaceBilled12M: whitespace.reduce(
        (sum, customer) => sum + (customer.billed12M ?? 0),
        0,
      ),
      currency:
        [...adopters.map((adopter) => adopter.customer), ...whitespace].find(
          (customer) => customer.primaryCurrency != null,
        )?.primaryCurrency ?? null,
    }
  })
}

/** One capability's slice of the book's billed value. */
export type PortfolioAreaValueRow = {
  area: string
  /**
   * 12-month billed revenue attributed to the area: each paying customer's
   * billed spread across the areas they use, proportional to their weighted
   * 28-day volume there. An attribution model — "the money works here" —
   * not invoice line items.
   */
  attributed: number
  /** Of that, money on high/medium churn-risk customers. */
  atRiskAttributed: number
  /** Share of the book's attributed revenue, 0–1. */
  revenueShare: number
  /** Share of the fleet's weighted 28-day volume, 0–1. */
  usageShare: number
  /** Paying customers (billed > 0) with tracked use of the area. */
  payingAdopters: number
}

export type PortfolioAreaValueBook = {
  /** Biggest money first; areas with usage but no revenue still appear. */
  rows: PortfolioAreaValueRow[]
  /** Total billed attributed to any area. */
  attributedTotal: number
  /**
   * Billed revenue that could not be attributed: paying customers with no
   * tracked area usage at all — money with no usage footprint behind it.
   */
  unattributed: number
  currency: string | null
}

/**
 * "Where the money works": spreads every paying customer's 12-month billed
 * revenue across the interest areas they actually use, weighted by their
 * 28-day volume per area, and compares each area's share of the money with
 * its share of the fleet's usage. Read the gaps: revenue share far above
 * usage share = a premium anchor carried by few big payers (protect it);
 * usage share far above revenue share = heavily used but under-monetized
 * (pricing/expansion opportunity).
 */
export function derivePortfolioAreaValue(
  customers: PortfolioCustomerSummary[],
): PortfolioAreaValueBook {
  const byArea = new Map<
    string,
    { attributed: number; atRisk: number; paying: number; usage: number }
  >()
  const getEntry = (area: string) => {
    const entry = byArea.get(area) ?? {
      attributed: 0,
      atRisk: 0,
      paying: 0,
      usage: 0,
    }
    byArea.set(area, entry)
    return entry
  }

  let usageTotal = 0
  let attributedTotal = 0
  let unattributed = 0
  let currency: string | null = null

  for (const customer of customers) {
    // The customer's 28-day volume per area (same source as the adopter
    // lists and the per-customer "What they use" card).
    const volumes = new Map<string, number>()
    let volumeTotal = 0
    for (const member of customer.members) {
      for (const topArea of member.TopAreas28d) {
        const volume = topArea.Weighted ?? topArea.Count
        volumes.set(topArea.Area, (volumes.get(topArea.Area) ?? 0) + volume)
        volumeTotal += volume
      }
    }
    for (const [area, volume] of volumes) {
      getEntry(area).usage += volume
      usageTotal += volume
    }

    const billed = customer.billed12M ?? 0
    if (billed <= 0) continue
    currency ??= customer.primaryCurrency
    if (volumeTotal <= 0) {
      unattributed += billed
      continue
    }

    attributedTotal += billed
    const churnLevel = customer.summary.churnRisk.level
    const atRisk = churnLevel === "high" || churnLevel === "medium"
    for (const [area, volume] of volumes) {
      const entry = getEntry(area)
      const slice = billed * (volume / volumeTotal)
      entry.attributed += slice
      if (atRisk) entry.atRisk += slice
      entry.paying += 1
    }
  }

  const rows: PortfolioAreaValueRow[] = [...byArea.entries()]
    .map(([area, entry]) => ({
      area,
      attributed: entry.attributed,
      atRiskAttributed: entry.atRisk,
      revenueShare:
        attributedTotal > 0 ? entry.attributed / attributedTotal : 0,
      usageShare: usageTotal > 0 ? entry.usage / usageTotal : 0,
      payingAdopters: entry.paying,
    }))
    .sort((a, b) => b.attributed - a.attributed || b.usageShare - a.usageShare)

  return { rows, attributedTotal, unattributed, currency }
}
